"use client"

import { useState } from "react"
import { BookingAppointment, formatPrice, formatDuration } from "@/data/booking-appointments"
import { ChevronDown, ChevronUp, Clock, CreditCard } from "lucide-react"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"

interface AppointmentOptionCardProps {
  appointment: BookingAppointment
  price?: number  // Location-specific price (if known)
  onSelect: () => void
}

/**
 * AppointmentOptionCard - Selectable card for appointment type
 *
 * Shows appointment name, duration, and price.
 * When price prop is provided, shows that specific price.
 * Has expandable "View details" section for full description.
 */
export function AppointmentOptionCard({
  appointment,
  price,
  onSelect,
}: AppointmentOptionCardProps) {
  const [isOpen, setIsOpen] = useState(false)

  // Use provided price, or show "varies" if not provided
  const priceDisplay = price !== undefined ? formatPrice(price) : "Price varies"

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden hover:border-[#848974] transition-colors">
      {/* Main clickable area */}
      <button
        onClick={onSelect}
        className="w-full px-4 py-2 text-left bg-gray-100 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-normal text-[#3f4141]">{appointment.name}</h3>
            <div className="flex items-center gap-4 mt-1 text-sm font-light text-gray-500">
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {formatDuration(appointment.duration)}
              </span>
              <span className="flex items-center gap-1">
                <CreditCard className="h-4 w-4" />
                {priceDisplay}
              </span>
            </div>
          </div>
          <div className="text-[#848974] font-medium text-sm">
            Select
          </div>
        </div>
      </button>

      {/* Expandable details section */}
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <button
            onClick={(e) => {
              e.stopPropagation()
              setIsOpen(!isOpen)
            }}
            className="w-full px-4 py-2 border-t border-gray-100 text-sm font-light text-gray-500 hover:text-gray-700 flex items-center justify-center gap-1 transition-colors"
          >
            {isOpen ? (
              <>
                Hide details
                <ChevronUp className="h-4 w-4" />
              </>
            ) : (
              <>
                View details
                <ChevronDown className="h-4 w-4" />
              </>
            )}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 pb-4 pt-2 text-sm text-gray-600 border-t border-gray-100 bg-gray-50">
            {appointment.description}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}
